import React from 'react';
import { Plus, Trash2, Code, Clock, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useWorkflowEditor } from './WorkflowEditorContext.shared';
import { JsonValue, WorkflowDefinitionVariable } from '@/types/workflow-editor';

function isJsonValue(value: unknown): value is JsonValue {
  if (
    value === null ||
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  ) {
    return true;
  }

  if (Array.isArray(value)) {
    return value.every(isJsonValue);
  }

  if (typeof value === 'object') {
    return Object.values(value).every(isJsonValue);
  }

  return false;
}

function parseJsonValue(value: string): JsonValue | null {
  const parsed: unknown = JSON.parse(value);
  return isJsonValue(parsed) ? parsed : null;
}

function parseVariableType(value: string): WorkflowDefinitionVariable['type'] {
  switch (value) {
    case 'string':
    case 'number':
    case 'boolean':
    case 'object':
    case 'date':
      return value;
    default:
      return 'string';
  }
}

function formatJsonValueForTextInput(value: JsonValue | undefined): string {
  if (value == null) {
    return '';
  }

  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  ) {
    return String(value);
  }

  return JSON.stringify(value);
}

function formatJsonValueForDisplay(value: JsonValue | undefined): string {
  if (value == null) {
    return 'Not set';
  }

  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  ) {
    return String(value);
  }

  return JSON.stringify(value);
}

function formatJsonValueForNumberInput(
  value: JsonValue | undefined,
): number | '' {
  return typeof value === 'number' ? value : '';
}

function formatJsonValueForDateTimeLocal(value: JsonValue | undefined): string {
  if (typeof value !== 'string' && typeof value !== 'number') {
    return '';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

function formatJsonValueForDateDisplay(value: JsonValue | undefined): string {
  if (typeof value !== 'string' && typeof value !== 'number') {
    return 'Not set';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return 'Invalid date';
  }

  return date.toLocaleString();
}

export const WorkflowVariablesTab: React.FC = () => {
  const {
    nodes,
    workflowVariables,
    setWorkflowVariables,
  } = useWorkflowEditor();

  return (
    <ScrollArea className="flex-1">
      <div className="p-4 space-y-4">
        {/* Add Variable Button */}
        <Button
          size="sm"
          className="w-full gap-2"
          onClick={() => {
            const newVar = {
              name: `var${workflowVariables.length + 1}`,
              type: 'string' as const,
              defaultValue: '',
            };
            setWorkflowVariables([...workflowVariables, newVar]);
          }}
        >
          <Plus className="w-4 h-4" />
          Add Variable
        </Button>

        {/* MQTT Trigger Variables Section */}
        {(() => {
          const startNode = nodes.find((n) => n.data?.type === 'start');
          const mqttMappings = startNode?.data?.config?.variableMappings || [];

          if (mqttMappings.length > 0) {
            return (
              <div className="space-y-2">
                <div className="flex items-center gap-2 px-2">
                  <div className="flex-1 h-px bg-green-200"></div>
                  <span className="text-xs font-semibold text-green-700 uppercase">MQTT Trigger Variables</span>
                  <div className="flex-1 h-px bg-green-200"></div>
                </div>
                <div className="bg-green-50 p-3 rounded border border-green-200 space-y-2">
                  <p className="text-xs text-green-800">
                    <strong>Auto-created from MQTT trigger mappings</strong>
                  </p>
                  {mqttMappings.map((mapping, idx: number) => (
                    <div key={idx} className="bg-white p-2 rounded border border-green-100">
                      <div className="flex items-start gap-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <code className="text-sm font-semibold bg-green-100 px-2 py-1 rounded">
                              {mapping.targetVariable}
                            </code>
                            <span className="inline-block px-2 py-0.5 text-xs bg-green-200 text-green-800 rounded">
                              MQTT
                            </span>
                          </div>
                          <div className="mt-1 text-xs text-gray-600">
                            <span className="text-gray-500">Source:</span>{' '}
                            <code className="bg-gray-100 px-1 py-0.5 rounded font-mono">
                              {mapping.sourcePath}
                            </code>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  <p className="text-xs text-green-700 pt-2 border-t border-green-200">
                    These variables are automatically populated when an MQTT message triggers the workflow
                  </p>
                </div>
              </div>
            );
          }
          return null;
        })()}

        {/* Manual Workflow Variables Section */}
        {workflowVariables.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 px-2">
              <div className="flex-1 h-px bg-blue-200"></div>
              <span className="text-xs font-semibold text-blue-700 uppercase">Workflow Variables</span>
              <div className="flex-1 h-px bg-blue-200"></div>
            </div>
          </div>
        )}

        {/* Variables List */}
        {workflowVariables.length === 0 ? (
          <div className="text-center text-muted-foreground py-8">
            <Code className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p className="text-sm">No variables defined</p>
            <p className="text-xs mt-1">Add variables to use in your workflow</p>
          </div>
        ) : (
          <div className="space-y-3">
            {workflowVariables.map((variable, index) => (
              <Card key={index}>
                <CardContent className="pt-4 space-y-3">
                  <div className="space-y-2">
                    <Label htmlFor={`var-name-${index}`}>Variable Name</Label>
                    <Input
                      id={`var-name-${index}`}
                      value={variable.name}
                      onChange={(e) => {
                        const updated = [...workflowVariables];
                        updated[index].name = e.target.value;
                        setWorkflowVariables(updated);
                      }}
                      placeholder="variable_name"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor={`var-type-${index}`}>Type</Label>
                    <select
                      id={`var-type-${index}`}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      value={variable.type}
                      onChange={(e) => {
                        const updated = [...workflowVariables];
                        const nextVariable = updated[index];
                        if (!nextVariable) return;
                        nextVariable.type = parseVariableType(e.target.value);
                        nextVariable.defaultValue = e.target.value === 'number' ? 0
                          : e.target.value === 'boolean' ? false
                          : e.target.value === 'object' ? {}
                          : e.target.value === 'date' ? new Date().toISOString()
                          : '';
                        setWorkflowVariables(updated);
                      }}
                    >
                      <option value="string">String</option>
                      <option value="number">Number</option>
                      <option value="boolean">Boolean</option>
                      <option value="object">Object/Array</option>
                      <option value="date">Date/Time</option>
                    </select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor={`var-default-${index}`}>Default Value</Label>
                    {variable.type === 'boolean' ? (
                      <select
                        id={`var-default-${index}`}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                        value={variable.defaultValue === true ? 'true' : 'false'}
                        onChange={(e) => {
                          const updated = [...workflowVariables];
                          updated[index].defaultValue = e.target.value === 'true';
                          setWorkflowVariables(updated);
                        }}
                      >
                        <option value="false">false</option>
                        <option value="true">true</option>
                      </select>
                    ) : variable.type === 'date' ? (
                      <div className="space-y-2">
                        <Input
                          id={`var-default-${index}`}
                          type="datetime-local"
                          value={formatJsonValueForDateTimeLocal(variable.defaultValue)}
                          onChange={(e) => {
                            const updated = [...workflowVariables];
                            updated[index].defaultValue = new Date(e.target.value).toISOString();
                            setWorkflowVariables(updated);
                          }}
                          className="font-mono text-sm"
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full gap-2"
                          onClick={() => {
                            const updated = [...workflowVariables];
                            updated[index].defaultValue = new Date().toISOString();
                            setWorkflowVariables(updated);
                          }}
                        >
                          <Clock className="w-4 h-4" />
                          Set to Current Time
                        </Button>
                        <p className="text-xs text-muted-foreground">
                          Current: {formatJsonValueForDateDisplay(variable.defaultValue)}
                        </p>
                      </div>
                    ) : variable.type === 'object' ? (
                      <Textarea
                        id={`var-default-${index}`}
                        value={typeof variable.defaultValue === 'string'
                          ? variable.defaultValue
                          : JSON.stringify(variable.defaultValue, null, 2)}
                        onChange={(e) => {
                          const updated = [...workflowVariables];
                          const nextVariable = updated[index];
                          if (!nextVariable) return;
                          try {
                            const parsedValue = parseJsonValue(e.target.value);
                            nextVariable.defaultValue = parsedValue ?? e.target.value;
                          } catch {
                            nextVariable.defaultValue = e.target.value;
                          }
                          setWorkflowVariables(updated);
                        }}
                        placeholder='{"key": "value"} or [1, 2, 3]'
                        rows={3}
                        className="font-mono text-xs"
                      />
                    ) : variable.type === 'number' ? (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id={`var-random-${index}`}
                            checked={variable.randomConfig?.enabled || false}
                            onChange={(e) => {
                              const updated = [...workflowVariables];
                              const nextVariable = updated[index];
                              if (!nextVariable) return;
                              if (e.target.checked) {
                                nextVariable.randomConfig = { enabled: true, min: 0, max: 100 };
                              } else {
                                nextVariable.randomConfig = undefined;
                              }
                              setWorkflowVariables(updated);
                            }}
                            className="w-4 h-4 rounded"
                          />
                          <Label htmlFor={`var-random-${index}`} className="text-sm">
                            Generate random number
                          </Label>
                        </div>

                        {variable.randomConfig?.enabled ? (
                          <div className="space-y-2 pl-6">
                            <div className="grid grid-cols-2 gap-2">
                              <div className="space-y-1">
                                <Label htmlFor={`var-random-min-${index}`} className="text-xs">Min</Label>
                                <Input
                                  id={`var-random-min-${index}`}
                                  type="number"
                                  value={variable.randomConfig.min}
                                  onChange={(e) => {
                                    const updated = [...workflowVariables];
                                    const nextVariable = updated[index];
                                    if (nextVariable?.randomConfig) {
                                      nextVariable.randomConfig.min = Number(e.target.value);
                                    }
                                    setWorkflowVariables(updated);
                                  }}
                                  className="h-8 text-sm"
                                />
                              </div>
                              <div className="space-y-1">
                                <Label htmlFor={`var-random-max-${index}`} className="text-xs">Max</Label>
                                <Input
                                  id={`var-random-max-${index}`}
                                  type="number"
                                  value={variable.randomConfig.max}
                                  onChange={(e) => {
                                    const updated = [...workflowVariables];
                                    const nextVariable = updated[index];
                                    if (nextVariable?.randomConfig) {
                                      nextVariable.randomConfig.max = Number(e.target.value);
                                    }
                                    setWorkflowVariables(updated);
                                  }}
                                  className="h-8 text-sm"
                                />
                              </div>
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              className="w-full gap-2"
                              onClick={() => {
                                const updated = [...workflowVariables];
                                const randomConfig = variable.randomConfig;
                                const nextVariable = updated[index];
                                if (!randomConfig || !nextVariable) return;
                                const { min, max } = randomConfig;
                                const randomValue = Math.floor(Math.random() * (max - min + 1)) + min;
                                nextVariable.defaultValue = randomValue;
                                setWorkflowVariables(updated);
                              }}
                            >
                              <Zap className="w-4 h-4" />
                              Generate Random
                            </Button>
                            <p className="text-xs text-muted-foreground">
                              Current: {formatJsonValueForDisplay(variable.defaultValue)}
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">
                              Expression: <code className="bg-gray-100 px-1 rounded">randomInt({variable.randomConfig.min}, {variable.randomConfig.max})</code>
                            </p>
                          </div>
                        ) : (
                          <Input
                            id={`var-default-${index}`}
                            type="number"
                            value={formatJsonValueForNumberInput(variable.defaultValue)}
                            onChange={(e) => {
                              const updated = [...workflowVariables];
                              const nextVariable = updated[index];
                              if (!nextVariable) return;
                              nextVariable.defaultValue = Number(e.target.value);
                              setWorkflowVariables(updated);
                            }}
                            placeholder="0"
                          />
                        )}
                      </div>
                    ) : (
                      <Input
                        id={`var-default-${index}`}
                        type="text"
                        value={formatJsonValueForTextInput(variable.defaultValue)}
                        onChange={(e) => {
                          const updated = [...workflowVariables];
                          const nextVariable = updated[index];
                          if (!nextVariable) return;
                          nextVariable.defaultValue = e.target.value;
                          setWorkflowVariables(updated);
                        }}
                        placeholder="default value"
                      />
                    )}
                  </div>

                  <Button
                    variant="destructive"
                    size="sm"
                    className="w-full gap-2"
                    onClick={() => {
                      const updated = workflowVariables.filter((_, i) => i !== index);
                      setWorkflowVariables(updated);
                    }}
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete Variable
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </ScrollArea>
  );
};
